/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import dayjs from "dayjs";
import { isValidCustomDateRange } from "./isValidCustomDateRange";

export const CLASSIFIED_DISCOUNT_REPORT_PATH =
  "/admin/reports/classified_discount_records";
export const CLASSIFIED_DISCOUNT_STATUSES = ["managed", "unmanaged", "unclassified"];
export const ALL_FILTER_VALUE = "All";
export const CLASSIFIED_DISCOUNT_DATE_FILTERS = [
  "All",
  "Today",
  "Yesterday",
  "This Week",
  "Last 7 Days",
  "This Month",
  "Last Month",
  "Custom Date",
];

const parseRestaurantIds = (searchParams) => {
  const restaurantIds = searchParams
    .getAll("restaurant_ids")
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);

  return restaurantIds;
};

const parseDate = (value) => {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue : null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split("/");
    const parsedValue = dayjs(`${year}-${month}-${day}`);
    return parsedValue.isValid() ? parsedValue : null;
  }

  return null;
};

const parseMaybeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && `${parsedValue}` === `${value}`
    ? parsedValue
    : value;
};

const normalizeDateFilterAlias = (dateFilter) => {
  const aliases = {
    "Last 7 days": "Last 7 Days",
    "Last month": "Last Month",
    Custom: "Custom Date",
  };

  return aliases[dateFilter] || dateFilter;
};

const normalizeDateValue = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs?.(value)) return value;

  if (value?.$d) {
    const reconstructedValue = dayjs(value.$d);
    return reconstructedValue.isValid() ? reconstructedValue : null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue : null;
};

export const buildClassifiedDiscountDrilldownSearch = ({
  status,
  restaurantGroupId,
  restaurantIds = [],
  dateFilter,
  customDateFrom,
  customDateTo,
  hasOtDiscount,
}) => {
  const searchParams = new URLSearchParams();

  if (CLASSIFIED_DISCOUNT_STATUSES.includes(status)) {
    searchParams.set("status", status);
  }

  if (restaurantGroupId && restaurantGroupId !== ALL_FILTER_VALUE) {
    searchParams.set("restaurant_group_id", restaurantGroupId);
  }

  restaurantIds.forEach((restaurantId) => {
    if (restaurantId && restaurantId !== ALL_FILTER_VALUE) {
      searchParams.append("restaurant_ids", restaurantId);
    }
  });

  if (dateFilter) {
    searchParams.set("date_filter", dateFilter);
  }

  if (dateFilter === "Custom Date" && isValidCustomDateRange(customDateFrom, customDateTo)) {
    const from = dayjs(customDateFrom);
    const to = dayjs(customDateTo);

    if (from.isValid() && to.isValid()) {
      searchParams.set("date_from", from.format("YYYY-MM-DD"));
      searchParams.set("date_to", to.format("YYYY-MM-DD"));
    }
  }

  if (hasOtDiscount === false) {
    searchParams.set("has_ot_discount", "false");
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

export const parseClassifiedDiscountDrilldownSearch = (search) => {
  const searchParams = new URLSearchParams(search);
  const requestedStatus = searchParams.get("status");
  const status = CLASSIFIED_DISCOUNT_STATUSES.includes(requestedStatus)
    ? requestedStatus
    : CLASSIFIED_DISCOUNT_STATUSES[0];

  const restaurantGroupId =
    parseMaybeNumber(searchParams.get("restaurant_group_id")) ||
    ALL_FILTER_VALUE;
  const restaurantIds = parseRestaurantIds(searchParams);
  const requestedDateFilter = searchParams.get("date_filter");
  const dateFilter = normalizeDateFilterAlias(requestedDateFilter);
  const dateFrom = parseDate(searchParams.get("date_from"));
  const dateTo = parseDate(searchParams.get("date_to"));
  const hasOtDiscount = searchParams.get("has_ot_discount") === "false"
    ? false
    : undefined;

  return {
    status,
    hadInvalidStatus:
      Boolean(requestedStatus) &&
      !CLASSIFIED_DISCOUNT_STATUSES.includes(requestedStatus),
    filters: {
      restaurant_group_id: restaurantGroupId,
      restaurant_ids:
        restaurantIds.length > 0 ? restaurantIds : [ALL_FILTER_VALUE],
      has_ot_discount: hasOtDiscount,
      dateFilter: dateFilter || (dateFrom && dateTo ? "Custom Date" : "Last 7 Days"),
      customDateFrom: dateFrom,
      customDateTo: dateTo,
    },
  };
};

export const getClassifiedDiscountApiFilters = (filters) => {
  const params = {};

  const today = dayjs();
  let dateRange = null;
  const customDateFrom = normalizeDateValue(filters?.customDateFrom);
  const customDateTo = normalizeDateValue(filters?.customDateTo);

  if (filters?.dateFilter !== "Custom Date" && filters?.dateFilter !== "All") {
    switch (filters?.dateFilter) {
      case "Today":
        dateRange = {
          from: today.format("DD/MM/YYYY"),
          to: today.format("DD/MM/YYYY"),
        };
        break;
      case "Yesterday":
        dateRange = {
          from: today.subtract(1, "day").format("DD/MM/YYYY"),
          to: today.subtract(1, "day").format("DD/MM/YYYY"),
        };
        break;
      case "This Week":
        dateRange = {
          from: today.startOf("week").format("DD/MM/YYYY"),
          to: today.endOf("week").format("DD/MM/YYYY"),
        };
        break;
      case "Last 7 Days":
        dateRange = {
          from: today.subtract(7, "day").format("DD/MM/YYYY"),
          to: today.format("DD/MM/YYYY"),
        };
        break;
      case "This Month":
        dateRange = {
          from: today.startOf("month").format("DD/MM/YYYY"),
          to: today.endOf("month").format("DD/MM/YYYY"),
        };
        break;
      case "Last Month":
        dateRange = {
          from: today
            .subtract(1, "month")
            .startOf("month")
            .format("DD/MM/YYYY"),
          to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
        };
        break;
      default:
        break;
    }
  } else if (filters?.dateFilter === "Custom Date" && isValidCustomDateRange(customDateFrom, customDateTo)) {
    dateRange = {
      from: customDateFrom.format("DD/MM/YYYY"),
      to: customDateTo.format("DD/MM/YYYY"),
    };
  }

  if (
    filters?.restaurant_group_id &&
    filters.restaurant_group_id !== ALL_FILTER_VALUE
  ) {
    params.restaurant_group_id = filters.restaurant_group_id;
  }

  if (
    Array.isArray(filters?.restaurant_ids) &&
    !filters.restaurant_ids.includes(ALL_FILTER_VALUE)
  ) {
    params.restaurant_ids = filters.restaurant_ids;
  }

  if (filters?.has_ot_discount === false) {
    params.has_ot_discount = false;
  }

  if (dateRange) {
    params.date_range = dateRange;
  }

  return params;
};
